import React, { useCallback, useState, useEffect } from 'react'
import { useModal } from 'react-modal-hook'
import * as Yup from 'yup'
import { VALIDATION_KEYS } from '../../utils/translation-constants'
import { Formik, Field, Form } from 'formik'
import InputError from '../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import NeoButton from '../../components/NeoButton/NeoButton'
import Modal from '../../components/Modal/Modal'
import { useHistory } from 'react-router-dom'
import { encryptAES } from '../../services/auth.service'
import quickLinkService from '../../services/quick-link.service'
import LayoutWithOutAccountCard from '../Layout/layout-without-card'
// import { useErrorHandler } from 'react-error-boundary';
import NotificationService from '../../services/notification.service'
import { CHANGE_PASSWORD_KEYS, BUTTON_KEYS } from '../../utils/translation-constants'
import { useTranslation } from 'react-i18next'
import validationConfig from 'custom/config/validation.config.json'
const changePasswordConfig = validationConfig['changePassword']

export default function ChangePassword() {
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const [criteriaList, setCriteriaList] = useState([])
  const [message, setMessage] = useState('')
  const [passwordPattern, setPasswordPattern] = useState(null)
  const [passwordLength, setPasswordLength] = useState()
  
  const { t } = useTranslation()
  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body '>
          <h6 className='text-center'>{message}</h6>
          <div className='modal-btn-style'>
            <NeoButton
              tabIndex='1'
              mode='newDesign'
              type='button'
              text={t(BUTTON_KEYS.OK)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  useEffect(() => {
    const getPwdCriteria = async () => {
      try {
        setLoading(true)
        const response = await quickLinkService.getPwdCriteria({})
        if (response.status === 200) {
          setCriteriaList(response.data['userPasswordCriteriaMap'])
          // const pwdMaxLength = response.data['userPasswordCriteria'][3].value
          // const pwdMinLength = response.data['userPasswordCriteria'][4].value
          // const pwdMinAlphabetLength = response.data['userPasswordCriteria'][0].value
          // const pwdMinNumberLength = response.data['userPasswordCriteria'][1].value
          // const pwdMinSplCharLength = response.data['userPasswordCriteria'][2].value
          const pwdMaxLength = response.data['userPasswordCriteriaMap']['MAX_PWD_LENGTH']
          const pwdMinLength = response.data['userPasswordCriteriaMap']['MIN_PWD_LENGTH']
          const pwdMinAlphabetLength = response.data['userPasswordCriteriaMap']['ALPHABETS']
          const pwdMinNumberLength = response.data['userPasswordCriteriaMap']['NUMERICS']
          const pwdMinSplCharLength = response.data['userPasswordCriteriaMap']['SPECIAL_CHARACTERS']
          const pattern = `(?=^.{${pwdMinLength},${pwdMaxLength}}$)(?=(.*\\d){${pwdMinNumberLength}})(?=(.*[A-Za-z]){${pwdMinAlphabetLength}})(?=(.*[\\+\\- \\= \\[\\]\\{ \\}\\;\\-\\(\\)\\<\\>\\_'!@#$%^&*?\"|/,.:\`~\\\\]){${pwdMinSplCharLength}})(?!.*[\\s])^.*`
          setPasswordPattern(pattern)
          setPasswordLength(pwdMaxLength)
          setLoading(false)
        }
      } catch (err) {
        setLoading(false)
        // //showModal()
      }
    }
    getPwdCriteria()
  }, [])

  // useErrorHandler(message);

  const postChangePassword = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      setLoading(true)
      setSubmitting(true)
      try {
        const request = {
          oldPassword: encryptAES(values.oldPassword),
          newPassword: encryptAES(values.newPassword),
        }
        const response = await quickLinkService.postChangePassword(request)
        if (response.status === 200) {
          NotificationService.showSuccessMessage(response.data['message'])
          history.push('/dashboard')
        }
        // setMessage(response.data.message)
        // //showModal()
        setLoading(false)
      } catch (err) {
        // setMessage(err.data.message)
        // //showModal()
        setLoading(false)
      }
      setSubmitting(false)
      resetForm()
    },
    [showModal],
  )

  function handleKeyDown(e) {
    if (e.key === ' ') {
      e.preventDefault()
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  const ChangePasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(VALIDATION_KEYS.NEW_PASSWORD_REQUIRED)
      .matches(new RegExp(passwordPattern), VALIDATION_KEYS.PASSWORD_MATCH_CRITERIA),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], VALIDATION_KEYS.CONFIRM_PASSWORD_NOT_MATCH)
      .required(VALIDATION_KEYS.CONFIRM_PASSWORD_REQUIRED),
    oldPassword: Yup.string().required(VALIDATION_KEYS.CURRENT_PASSWORD_REQUIRED),
  })

  return (
    <LayoutWithOutAccountCard
      breadCumKey='CHANGE_PASSWORD'
      keyPath={'CHANGE_PASSWORD'}
      showEmptySpace={true}>
      <div>
        <Formik
          initialValues={{
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
          }}
          validationSchema={ChangePasswordSchema}
          onSubmit={postChangePassword}>
          {({ isSubmitting, errors, touched }) => (
            <Form className='new-design-form-style'>
              <div className='heading-style'>{t(CHANGE_PASSWORD_KEYS.CHANGE_PASSWORD)}</div>
              <div className='d-flex row'>
                <div className='col-12'>
                  <div className='form-group my-2'>
                    <label className='control-label'>
                      {t(CHANGE_PASSWORD_KEYS.CURRENT_PASSWORD)}{' '}
                      {changePasswordConfig['isCurrentPasswordRequired'] ? '*' : ''}
                    </label>
                    <Field
                      tabIndex={"1"}
                      as='input'
                      name='oldPassword'
                      className='form-control'
                      type='password'
                    />
                    <InputError text={errors.oldPassword} touched={touched.oldPassword} />
                  </div>
                  <div className='form-group my-2'>
                    <label className='control-label'>
                      {t(CHANGE_PASSWORD_KEYS.NEW_PASSWORD)}{' '}
                      {changePasswordConfig['isNewPasswordRequired'] ? '*' : ''}
                    </label>
                    <Field
                      tabIndex={'2'}
                      as='input'
                      name='newPassword'
                      className='form-control'
                      type='password'
                      onKeyDown={handleKeyDown}
                      maxLength={passwordLength}
                    />
                    <InputError text={errors.newPassword} touched={touched.newPassword} />
                  </div>
                  <div className='form-group my-2'>
                    <label className='control-label'>
                      {t(CHANGE_PASSWORD_KEYS.CONFIRM_NEW_PASSWORD)}{' '}
                      {changePasswordConfig['isConfirmNewPasswordRequired'] ? '*' : ''}
                    </label>
                    <Field
                      tabIndex={'3'}
                      as='input'
                      name='confirmPassword'
                      className='form-control'
                      type='password'
                      maxLength={passwordLength}
                    />
                    <InputError
                      text={errors.confirmPassword}
                      touched={touched.confirmPassword}
                      type='password'
                    />
                  </div>
                  <div className='mt-4 ml-auto px-0'>
                    {isSubmitting ? (
                      renderLoading()
                    ) : (
                      <NeoButton
                        tabIndex='4'
                        type='submit'
                        disabled={isSubmitting}
                        text={t(BUTTON_KEYS.SUBMIT)}
                        mode='newDesign'
                      />
                    )}
                  </div>
                </div>
                <div className='col-12  mt-4'>
                  <div className='card passwordCriteria'>
                    <div className='card-body'>
                      <h6 className='card-title row mx-3'>
                        {t(CHANGE_PASSWORD_KEYS.PASSWORD_CRITERIA)}
                      </h6>
                      {/* {criteriaList.map((value, i) => (
                        <p className='card-text col' key={i}>
                          {value.description} : <b>{value.value}</b>
                        </p>
                      ))} */}
                      {Object.keys(criteriaList).map((key, i) => (
                        <p key={i} className='card-text col'>
                          {t(key)} : <b>{criteriaList[key]}</b>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </LayoutWithOutAccountCard>
  )
}
